import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';
import { CheckCircle } from 'lucide-react';

const PeerReviewPage = () => (
  <StaticPageLayout
    overline="About"
    title="Peer Review Process"
    description="All submissions undergo a structured and standards-driven peer review process. Our approach ensures scientific rigor, editorial fairness, and transparent communication with authors."
    breadcrumbs={[{ label: 'Peer Review Process', path: '/peer-review' }]}
  >
    <div className="space-y-10">
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Review Model</h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          MAH Quantum Research Institute employs a <strong>double-blind peer review</strong> model, in which neither the authors nor the reviewers are aware of each other's identities. This approach is designed to eliminate bias and ensure that manuscripts are evaluated solely on their scientific merit.
        </p>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          All submissions undergo a structured and standards-driven peer review process. Reviewers are selected based on their domain expertise, publication history, and absence of conflicts of interest with the submitting authors.
        </p>
        <p className="text-[#4B5563] leading-relaxed">
          In exceptional cases where double-blind review is not practicable (e.g., pre-registered studies with publicly available protocols), the editorial team will discuss the appropriate review model with authors prior to assignment.
        </p>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-6 pb-3 border-b border-[#E5E7EB]">Review Stages</h2>
        <div className="space-y-3">
          {[
            { step: '01', title: 'Initial Editorial Screening', desc: 'Upon submission, the editorial team conducts an initial assessment of the manuscript for scope alignment, formatting compliance, and basic quality threshold before proceeding to peer review.' },
            { step: '02', title: 'Reviewer Assignment', desc: 'Qualified domain experts are identified and invited to review the manuscript. Reviewers are selected on the basis of expertise, publication track record, and conflict-of-interest screening.' },
            { step: '03', title: 'Peer Review Evaluation', desc: 'Assigned reviewers assess the manuscript against defined criteria including scientific rigor, originality, methodology, data analysis, and presentation quality.' },
            { step: '04', title: 'Editorial Decision', desc: 'The editor-in-chief reviews reviewer reports and issues one of four decisions: Accept, Minor Revision, Major Revision, or Reject. All decisions are communicated with structured feedback.' },
            { step: '05', title: 'Revision & Final Decision', desc: 'Where revisions are requested, authors are given a structured timeline to respond. Revised manuscripts may undergo a second round of review before a final decision is issued.' },
          ].map(item => (
            <div key={item.step} className="flex gap-4 border border-[#E5E7EB] p-5 bg-white">
              <span className="font-serif text-2xl font-bold text-[#E5E7EB] leading-none flex-shrink-0">{item.step}</span>
              <div>
                <h3 className="font-semibold text-[#1F1F1F] text-sm mb-1">{item.title}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Review Timeline</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Initial Screening', value: '3–5 business days' },
            { label: 'Peer Review Period', value: '4–8 weeks' },
            { label: 'Decision Notification', value: 'Within 2 weeks of review completion' },
          ].map(item => (
            <div key={item.label} className="bg-[#F5F7FA] border border-[#E5E7EB] px-4 py-4 text-center">
              <p className="font-serif text-lg font-semibold text-[#1A73E8]">{item.value}</p>
              <p className="text-xs text-[#6B7280] mt-1">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Reviewer Responsibilities</h2>
        <ul className="space-y-2">
          {[
            'Evaluate the scientific validity and rigor of the research',
            'Assess the originality and contribution of the work to the field',
            'Review the appropriateness and accuracy of methodology and data analysis',
            'Identify any ethical concerns, data integrity issues, or potential conflicts',
            'Provide constructive, specific, and professional written feedback',
            'Maintain strict confidentiality of the manuscript throughout the process',
          ].map(item => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-[#4B5563]">
              <CheckCircle size={14} className="text-[#1A73E8] flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </section>
    </div>
  </StaticPageLayout>
);

export default PeerReviewPage;
